import React, { useState } from 'react';
import { RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';

const PriceUpdateView = ({
  currentPrices,
  setCurrentPrices,
  usdClpRate,
  setUsdClpRate,
  operations,
  formatCurrency,
  onPriceUpdated
}) => {
  const [newPrice, setNewPrice] = useState('');
  const [selectedAsset, setSelectedAsset] = useState('');
  const [newUsdClpRate, setNewUsdClpRate] = useState(usdClpRate.toString());

  // Get unique assets from operations
  const uniqueAssets = [...new Set(operations
    .filter(op => op.assetType !== 'USD Purchase' && op.assetType !== 'Time Deposit' && op.assetType !== 'Liquidity')
    .map(op => op.name)
  )];

  const handleUpdatePrice = (e) => {
    e.preventDefault();
    if (!selectedAsset || !newPrice) {
      alert('Por favor selecciona un activo y ingresa un precio');
      return;
    }

    setCurrentPrices(prev => ({
      ...prev,
      [selectedAsset]: parseFloat(newPrice)
    }));

    if (onPriceUpdated) onPriceUpdated(selectedAsset, parseFloat(newPrice));

    setNewPrice('');
    setSelectedAsset('');
  };

  const handleUpdateUsdClp = (e) => {
    e.preventDefault();
    const rate = parseFloat(newUsdClpRate);
    if (!rate || rate <= 0) {
      alert('Por favor ingresa un tipo de cambio válido');
      return;
    }

    setUsdClpRate(rate);
  };

  const calculateGainLoss = (asset, currentPrice, originalPrice) => {
    if (!currentPrice || !originalPrice) return 0;
    return ((currentPrice - originalPrice) / originalPrice) * 100;
  };

  const getAssetOperations = (assetName) => {
    return operations.filter(op => op.name === assetName && op.type === 'Buy');
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#041627] mb-2 font-headline">Actualizar Valores</h1>
        <p className="text-sm text-slate-500">Mantén actualizados los precios de tus activos y el tipo de cambio USD/CLP</p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        {/* Update USD/CLP Rate */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="font-bold text-lg mb-4 text-[#041627] flex items-center gap-2">
              <RefreshCw size={20} />
              Tipo de Cambio USD/CLP
            </h3>
            <form onSubmit={handleUpdateUsdClp} className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-[#44474c]">Valor Actual</label>
                <input
                  type="number"
                  step="0.01"
                  value={newUsdClpRate}
                  onChange={(e) => setNewUsdClpRate(e.target.value)}
                  placeholder="942.50"
                  className="bg-[#f2f4f7] border-none rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#041627]/10"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-[#041627] text-white py-3 rounded-lg font-bold hover:opacity-90 transition-all"
              >
                Actualizar Tipo de Cambio
              </button>
            </form>
            <div className="mt-4 p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Valor actual:</p>
              <p className="text-lg font-bold text-[#041627]">{usdClpRate.toFixed(2)} CLP</p>
            </div>
          </div>
        </div>

        {/* Update Asset Prices */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
            <h3 className="font-bold text-lg mb-4 text-[#041627] flex items-center gap-2">
              <TrendingUp size={20} />
              Precios de Activos
            </h3>
            <form onSubmit={handleUpdatePrice} className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Activo</label>
                  <select
                    value={selectedAsset}
                    onChange={(e) => setSelectedAsset(e.target.value)}
                    className="bg-[#f2f4f7] border-none rounded-lg p-3 text-sm"
                    required
                  >
                    <option value="">Seleccionar activo...</option>
                    {uniqueAssets.map(asset => (
                      <option key={asset} value={asset}>{asset}</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-[#44474c]">Precio Actual (USD)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    placeholder="450.25"
                    className="bg-[#f2f4f7] border-none rounded-lg p-3 text-sm focus:ring-2 focus:ring-[#041627]/10"
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                className="bg-[#041627] text-white px-6 py-3 rounded-lg font-bold hover:opacity-90 transition-all"
              >
                Actualizar Precio
              </button>
            </form>

            {/* Current Prices List */}
            <div className="space-y-3">
              <h4 className="font-semibold text-[#041627]">Precios Actuales</h4>
              {uniqueAssets.length === 0 ? (
                <p className="text-sm text-slate-500">No hay activos registrados en operaciones</p>
              ) : (
                <div className="space-y-2">
                  {uniqueAssets.map((asset) => {
                    const price = currentPrices[asset];
                    const assetOps = getAssetOperations(asset);
                    const avgPurchasePrice = assetOps.length > 0
                      ? assetOps.reduce((sum, op) => sum + parseFloat(op.price), 0) / assetOps.length
                      : 0;
                    const gainLoss = calculateGainLoss(asset, price, avgPurchasePrice);

                    return (
                      <div key={asset} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-semibold text-[#041627]">{asset}</p>
                          <p className="text-xs text-slate-500">
                            Compra promedio: {formatCurrency(avgPurchasePrice)}
                          </p>
                        </div>
                        <div className="text-right">
                          {price ? (
                            <>
                              <p className="font-bold text-[#041627]">{formatCurrency(price)}</p>
                              {avgPurchasePrice > 0 && (
                                <p className={`text-xs flex items-center gap-1 justify-end ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {gainLoss >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                  {gainLoss >= 0 ? '+' : ''}{gainLoss.toFixed(2)}%
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-xs text-slate-400 italic">Sin precio actualizado</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
        <h3 className="font-bold text-lg mb-4 text-[#041627]">Resumen de Valoración</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-slate-500">Total Invertido</p>
            <p className="text-2xl font-bold text-[#041627]">
              {formatCurrency(operations
                .filter(op => op.type === 'Buy')
                .reduce((sum, op) => sum + (parseFloat(op.quantity) * parseFloat(op.price)), 0)
              )}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-slate-500">Valor Actual</p>
            <p className="text-2xl font-bold text-[#041627]">
              {formatCurrency(operations
                .filter(op => op.type === 'Buy' && currentPrices[op.name])
                .reduce((sum, op) => sum + (parseFloat(op.quantity) * currentPrices[op.name]), 0)
              )}
            </p>
          </div>
          <div className="text-center">
            <p className="text-sm text-slate-500">Ganancia/Pérdida</p>
            <p className={`text-2xl font-bold ${
              operations.filter(op => op.type === 'Buy' && currentPrices[op.name])
                .reduce((sum, op) => {
                  const currentValue = parseFloat(op.quantity) * currentPrices[op.name];
                  const purchaseValue = parseFloat(op.quantity) * parseFloat(op.price);
                  return sum + (currentValue - purchaseValue);
                }, 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {formatCurrency(operations
                .filter(op => op.type === 'Buy' && currentPrices[op.name])
                .reduce((sum, op) => {
                  const currentValue = parseFloat(op.quantity) * currentPrices[op.name];
                  const purchaseValue = parseFloat(op.quantity) * parseFloat(op.price);
                  return sum + (currentValue - purchaseValue);
                }, 0)
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceUpdateView;